import { useState, useEffect } from 'react';
import { productsAPI, materialsAPI, userSettingsAPI } from '../../lib/api';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { toast } from 'sonner';
import { Plus, Loader2, Package, X } from 'lucide-react';

const SKILL_LEVELS = [
  { value: 'beginner', label: 'Beginner', multiplier: 1.0 },
  { value: 'intermediate', label: 'Intermediate', multiplier: 1.15 },
  { value: 'advanced', label: 'Advanced', multiplier: 1.35 },
  { value: 'master', label: 'Master', multiplier: 1.6 },
];

const DEFAULT_CATEGORIES = [
  'jewelry', 'pottery', 'textiles', 'woodwork', 'candles', 
  'soap', 'art', 'leather', 'glass', 'paper', 'food', 'other'
];

const UNIT_TYPES = [
  { value: 'each', label: 'Each' },
  { value: 'yard', label: 'Yard' },
  { value: 'foot', label: 'Foot' },
  { value: 'inch', label: 'Inch' },
  { value: 'oz', label: 'Ounce' },
  { value: 'lb', label: 'Pound' },
  { value: 'gram', label: 'Gram' },
  { value: 'ml', label: 'Milliliter' },
  { value: 'sheet', label: 'Sheet' },
  { value: 'roll', label: 'Roll' },
];

// Quick Add Material Dialog
export const QuickAddMaterial = ({ open, onOpenChange, onMaterialCreated }) => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    cost_per_unit: '',
    unit: 'each',
    current_stock: '',
    supplier: '',
  });

  const resetForm = () => {
    setFormData({
      name: '',
      cost_per_unit: '',
      unit: 'each',
      current_stock: '',
      supplier: '',
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.cost_per_unit) {
      toast.error('Please fill in material name and cost');
      return;
    }

    try {
      setLoading(true);
      const response = await materialsAPI.create({
        name: formData.name,
        cost_per_unit: parseFloat(formData.cost_per_unit),
        unit: formData.unit,
        current_stock: formData.current_stock ? parseFloat(formData.current_stock) : 0,
        supplier: formData.supplier || '',
        reorder_threshold: 0,
      });
      
      toast.success(`Material "${formData.name}" created!`);
      onMaterialCreated?.(response.data);
      resetForm();
      onOpenChange(false);
    } catch (error) {
      toast.error('Failed to create material');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Package className="w-5 h-5 text-[#1E3A5F]" />
            Quick Add Material
          </DialogTitle>
          <DialogDescription>
            Add a new material without leaving this page
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="material-name">Material Name *</Label>
            <Input
              id="material-name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="e.g., Sterling Silver Wire"
              data-testid="quick-material-name"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="material-cost">Cost per Unit *</Label>
              <Input
                id="material-cost"
                type="number"
                step="0.01"
                value={formData.cost_per_unit}
                onChange={(e) => setFormData({ ...formData, cost_per_unit: e.target.value })}
                placeholder="0.00"
                data-testid="quick-material-cost"
              />
            </div>
            <div>
              <Label htmlFor="material-unit">Unit</Label>
              <Select
                value={formData.unit}
                onValueChange={(value) => setFormData({ ...formData, unit: value })}
              >
                <SelectTrigger id="material-unit" data-testid="quick-material-unit">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {UNIT_TYPES.map((unit) => (
                    <SelectItem key={unit.value} value={unit.value}>
                      {unit.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="material-stock">Current Stock</Label>
              <Input
                id="material-stock"
                type="number"
                step="0.01"
                value={formData.current_stock}
                onChange={(e) => setFormData({ ...formData, current_stock: e.target.value })}
                placeholder="0"
                data-testid="quick-material-stock"
              />
            </div>
            <div>
              <Label htmlFor="material-supplier">Supplier</Label>
              <Input
                id="material-supplier"
                value={formData.supplier}
                onChange={(e) => setFormData({ ...formData, supplier: e.target.value })}
                placeholder="Optional"
              />
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button 
              type="submit" 
              disabled={loading}
              className="bg-[#1E3A5F] hover:bg-[#2d4a6f]"
              data-testid="quick-material-submit"
            >
              {loading ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Creating...</>
              ) : (
                <><Plus className="w-4 h-4 mr-2" /> Add Material</>
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// Quick Add Product Dialog
export const QuickAddProduct = ({ open, onOpenChange, onProductCreated }) => {
  const [loading, setLoading] = useState(false);
  const [materials, setMaterials] = useState([]);
  const [categories, setCategories] = useState(DEFAULT_CATEGORIES);
  const [showMaterialDialog, setShowMaterialDialog] = useState(false);
  
  const [formData, setFormData] = useState({
    name: '',
    category: '',
    retail_price: '',
    wholesale_price: '',
    labor_minutes: '',
    skill_level: 'intermediate',
    materials: [],
    description: '',
  });

  const [bomItem, setBomItem] = useState({ material_id: '', quantity: '' });

  useEffect(() => {
    if (open) {
      fetchData();
    }
  }, [open]);

  const fetchData = async () => {
    try {
      const [materialsRes, categoriesRes] = await Promise.all([
        materialsAPI.getAll(),
        userSettingsAPI.get('product_categories').catch(() => ({ data: { values: DEFAULT_CATEGORIES } }))
      ]);
      setMaterials(materialsRes.data);
      setCategories(categoriesRes.data.values || DEFAULT_CATEGORIES);
    } catch (error) {
      console.error('Failed to fetch data for quick add product');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      category: '',
      retail_price: '',
      wholesale_price: '',
      labor_minutes: '',
      skill_level: 'intermediate',
      materials: [],
      description: '',
    });
    setBomItem({ material_id: '', quantity: '' });
  };

  const handleAddBomItem = () => {
    if (!bomItem.material_id || !bomItem.quantity) return;
    
    const material = materials.find(m => m.id === bomItem.material_id);
    if (!material) return;

    setFormData(prev => ({
      ...prev,
      materials: [
        ...prev.materials,
        {
          material_id: bomItem.material_id,
          material_name: material.name,
          quantity: parseFloat(bomItem.quantity),
          unit: material.unit,
          cost_per_unit: material.cost_per_unit,
        }
      ]
    }));
    setBomItem({ material_id: '', quantity: '' });
  };

  const handleRemoveBomItem = (index) => {
    setFormData(prev => ({
      ...prev,
      materials: prev.materials.filter((_, i) => i !== index)
    }));
  };

  const handleMaterialCreated = (newMaterial) => {
    setMaterials(prev => [...prev, newMaterial]);
    // Auto-select the new material
    setBomItem({ material_id: newMaterial.id, quantity: '' });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.retail_price) {
      toast.error('Please fill in product name and retail price');
      return;
    }

    try {
      setLoading(true);
      const response = await productsAPI.create({
        name: formData.name,
        category: formData.category || 'other',
        retail_price: parseFloat(formData.retail_price),
        wholesale_price: formData.wholesale_price ? parseFloat(formData.wholesale_price) : null,
        labor_minutes: formData.labor_minutes ? parseInt(formData.labor_minutes) : 0,
        skill_level: formData.skill_level,
        materials: formData.materials,
        description: formData.description,
      });
      
      toast.success(`Product "${formData.name}" created!`);
      onProductCreated?.(response.data);
      resetForm();
      onOpenChange(false);
    } catch (error) {
      toast.error('Failed to create product');
    } finally {
      setLoading(false);
    }
  };

  const materialsCost = formData.materials.reduce(
    (sum, m) => sum + (m.quantity * m.cost_per_unit), 0
  );

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Package className="w-5 h-5 text-[#1E3A5F]" />
              Quick Add Product
            </DialogTitle>
            <DialogDescription>
              Add a new product without leaving this page
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="product-name">Product Name *</Label>
              <Input
                id="product-name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g., Silver Hoop Earrings"
                data-testid="quick-product-name"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="product-category">Category</Label>
                <Select
                  value={formData.category}
                  onValueChange={(value) => setFormData({ ...formData, category: value })}
                >
                  <SelectTrigger id="product-category" data-testid="quick-product-category">
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map((cat) => (
                      <SelectItem key={cat} value={cat}>
                        {cat.charAt(0).toUpperCase() + cat.slice(1)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="product-skill">Skill Level</Label>
                <Select
                  value={formData.skill_level}
                  onValueChange={(value) => setFormData({ ...formData, skill_level: value })}
                >
                  <SelectTrigger id="product-skill">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {SKILL_LEVELS.map((level) => (
                      <SelectItem key={level.value} value={level.value}>
                        {level.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="product-retail">Retail Price *</Label>
                <Input
                  id="product-retail"
                  type="number"
                  step="0.01"
                  value={formData.retail_price}
                  onChange={(e) => setFormData({ ...formData, retail_price: e.target.value })}
                  placeholder="0.00"
                  data-testid="quick-product-price"
                />
              </div>
              <div>
                <Label htmlFor="product-labor">Labor (minutes)</Label>
                <Input
                  id="product-labor"
                  type="number"
                  value={formData.labor_minutes}
                  onChange={(e) => setFormData({ ...formData, labor_minutes: e.target.value })}
                  placeholder="0"
                />
              </div>
            </div>

            {/* Materials (Bill of Materials) */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label>Materials (Optional)</Label>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowMaterialDialog(true)}
                  className="text-[#1E3A5F] h-7 text-xs"
                  data-testid="quick-add-material-btn"
                >
                  <Plus className="w-3 h-3 mr-1" /> New Material
                </Button>
              </div>
              
              <div className="flex gap-2 mb-2">
                <Select
                  value={bomItem.material_id}
                  onValueChange={(value) => setBomItem({ ...bomItem, material_id: value })}
                >
                  <SelectTrigger className="flex-1" data-testid="quick-product-material-select">
                    <SelectValue placeholder="Select material" />
                  </SelectTrigger>
                  <SelectContent>
                    {materials.map((material) => (
                      <SelectItem key={material.id} value={material.id}>
                        {material.name} (${material.cost_per_unit?.toFixed(2)}/{material.unit})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Input
                  type="number"
                  step="0.01"
                  value={bomItem.quantity}
                  onChange={(e) => setBomItem({ ...bomItem, quantity: e.target.value })}
                  placeholder="Qty"
                  className="w-20"
                />
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={handleAddBomItem}
                  disabled={!bomItem.material_id || !bomItem.quantity}
                >
                  <Plus className="w-4 h-4" />
                </Button>
              </div>

              {formData.materials.length > 0 && (
                <div className="border rounded-lg divide-y text-sm">
                  {formData.materials.map((item, idx) => (
                    <div key={idx} className="flex items-center justify-between p-2">
                      <span>{item.material_name}</span>
                      <div className="flex items-center gap-2">
                        <span className="text-gray-500">
                          {item.quantity} {item.unit} × ${item.cost_per_unit.toFixed(2)}
                        </span>
                        <span className="font-medium">
                          ${(item.quantity * item.cost_per_unit).toFixed(2)}
                        </span>
                        <button
                          type="button"
                          onClick={() => handleRemoveBomItem(idx)}
                          className="text-red-500 hover:text-red-700"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                  <div className="flex justify-between p-2 bg-gray-50 font-medium">
                    <span>Materials Cost</span>
                    <span>${materialsCost.toFixed(2)}</span>
                  </div>
                </div>
              )}
            </div>

            <div>
              <Label htmlFor="product-description">Description (Optional)</Label>
              <Textarea
                id="product-description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Brief product description..."
                rows={2}
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button 
                type="submit" 
                disabled={loading}
                className="bg-[#1E3A5F] hover:bg-[#2d4a6f]"
                data-testid="quick-product-submit"
              >
                {loading ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Creating...</>
                ) : (
                  <><Plus className="w-4 h-4 mr-2" /> Add Product</>
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Nested Quick Add Material Dialog */}
      <QuickAddMaterial
        open={showMaterialDialog}
        onOpenChange={setShowMaterialDialog}
        onMaterialCreated={handleMaterialCreated}
      />
    </>
  );
};

export default { QuickAddProduct, QuickAddMaterial };
